import { useQuery } from 'react-query';

import { ExportStatus } from '../../../../backend/src/types/types';
import { QueryEntity } from '../types';
import useGetFetchConfig from '../useGetFetchConfig';

export interface ExportTerminalStatus {
  status: ExportStatus;
  error?: string;
}

interface UseGetExportStatusArgs {
  /** Full status endpoint URL (page-local — caller appends `?exportId=...`). */
  statusEndpoint: string | undefined;
  exportId: string | null;
  enabled: boolean;
}

const POLL_INTERVAL_MS = 10_000;

/**
 * Polls the persisted terminal-status endpoint for an async export. Used by the
 * progress modal as a fallback when the SSE channel fails — returns `null`
 * while the job is still running, otherwise `{status, error?}`.
 */
export const useGetExportStatus = ({ statusEndpoint, exportId, enabled }: UseGetExportStatusArgs) => {
  const { fetchConfigGET } = useGetFetchConfig();
  const isEnabled = enabled && !!statusEndpoint && !!exportId;

  return useQuery<ExportTerminalStatus | null>(
    [QueryEntity.ExportStatus, { statusEndpoint, exportId }],
    async () => {
      const url = `${statusEndpoint}?exportId=${exportId}`;
      const res = await fetch(url, fetchConfigGET);
      // Treat any non-2xx as "still pending" so transient backend errors don't
      // spam the console — the next tick will try again.
      if (!res.ok) return null;
      const text = await res.text();
      if (!text) return null;
      try {
        return JSON.parse(text) as ExportTerminalStatus | null;
      } catch {
        return null;
      }
    },
    {
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      refetchInterval: isEnabled ? POLL_INTERVAL_MS : false,
      retry: false,
    },
  );
};
